// @ts-ignore: TODO: either don't import or move to @pathwright/ux
import { useObserveSizeContext } from "@pathwright/ui/src/components/observers/ObserveSizeContext"

// TODO: either don't import or move to @pathwright/ux
import { useToggleIntercomUI } from "@pathwright/web/src/modules/intercom/IntercomProvider"

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  UseDisclosureReturn
} from "@chakra-ui/react"
import { useScrollApp } from "@pathwright/utils/src/scroll/useScroll"
import { useMotionValueEvent } from "framer-motion"
import React from "react"
import AugmentationPanelNav from "./AugmentationPanelNav"
import { useToolbarPosition } from "./AugmentationPositionedToolbar"
import { AugmentationToolbarHookReturn } from "./AugmentationToolbar"
import AugmentationToolbarRouting, {
  useAugmentationQueryParamDisclosure
} from "./AugmentationToolbarRouting"
import useAugmentationPanel, {
  UseAugmentationPanelProps
} from "./useAugmentationPanel"

type AugmentationRoutedPanelProps = {
  toolbar: AugmentationToolbarHookReturn
  disclosure: UseDisclosureReturn
} & UseAugmentationPanelProps

const AugmentationRoutedPanel = React.forwardRef<
  HTMLElement,
  AugmentationRoutedPanelProps
>(({ toolbar, disclosure, gutter, offset, allowOverlay }, ref) => {
  // Calculating the top offset based on the navbar height and the window
  // scroll y value. We basically don't want to offset the panel if the navbar
  // is out of view.
  const [scrollY, setScrollY] = React.useState(0)
  const scroll = useScrollApp()
  useMotionValueEvent(scroll.scrollY, "change", () => {
    const nextScrollY = scroll.scrollY.get()
    if (Math.min(scrollY, nextScrollY) < navbarHeight) setScrollY(nextScrollY)
  })

  const { getRectValue } = useObserveSizeContext()
  const navbarHeight = getRectValue("navbar.height")
  const alertHeight = getRectValue("alert.height")
  const topOffset = Math.max(navbarHeight - scrollY, 0) + alertHeight

  // Since we render the routes inside the drawer, we need to
  // use this hook to open the drawer initially based on query param.
  useAugmentationQueryParamDisclosure({
    disclosure,
    queryParamKey: "panel"
  })

  // Hardcoding the bottom offset based on the (expected) toolbar top.
  // We could also use the ObserveSizeContext but there's currently a
  // bug where subsequent renders add to the rect size over time (should
  // remain the same)
  const bottomOffset = 60

  // Offsetting the panel based on the toolbar position.
  const { bottom, top } = useToolbarPosition(
    {
      bottom: { bottom: bottomOffset, top: 0 },
      side: { bottom: 0, top: topOffset }
    },
    allowOverlay
  )

  // Hide Intercom UI when drawer is open.
  useToggleIntercomUI(disclosure?.isOpen)

  const { getDrawerProps, getDrawerContentProps } = useAugmentationPanel({
    disclosure,
    offset: { ...offset, top, bottom },
    allowOverlay,
    gutter
  })

  return (
    <Drawer {...getDrawerProps()}>
      <DrawerContent
        ref={ref}
        {...getDrawerContentProps()}
        bg="whiteAlpha.600"
        backdropFilter="blur(20px)"
        borderRadius="card"
      >
        <AugmentationToolbarRouting
          toolbar={toolbar}
          queryParamKey="panel"
          renderConstant={() => (
            <DrawerHeader p={2}>
              <AugmentationPanelNav toolbar={toolbar} disclosure={disclosure} />
            </DrawerHeader>
          )}
          renderOutlet={(outlet) => <DrawerBody p={2}>{outlet}</DrawerBody>}
        />
      </DrawerContent>
    </Drawer>
  )

  // Ideally we could render the router/routes above the drawer so that
  // the routes are in full control of the drawer, but the hitch is that
  // the drawer is unmounted/mounted when routes change (and animates out/in).
  // If we can prevent the animation, we could try this approach again.
  // Chakra UI v3 allows the motionPreset="none" Drawer prop which would
  // prevent animation.
  // return (
  //   <AugmentationToolbarRouting toolbar={toolbar} queryParamKey="panel">
  //     {(outlet) => (
  //       <Drawer {...getDrawerProps()}>
  //         <DrawerContent
  //           ref={ref}
  //           {...getDrawerContentProps()}
  //           bg="whiteAlpha.600"
  //           backdropFilter="blur(20px)"
  //           borderRadius="20px"
  //         >
  //           <DrawerHeader p={2}>
  //             <AugmentationRoutedPanelHeader
  //               toolbar={toolbar}
  //               disclosure={disclosure}
  //             />
  //           </DrawerHeader>
  //           <DrawerBody p={2}>{outlet}</DrawerBody>
  //         </DrawerContent>
  //       </Drawer>
  //     )}
  //   </AugmentationToolbarRouting>
  // )
})

export default AugmentationRoutedPanel
